import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders, hasAdminCredentials } from '../../utils/adminAuth';
import { entriesUrl, removeFixtureUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import {
  clearFixtureCaches,
  FixturesTableName,
  formatFixtureLabel,
  getFixtureId,
  FixtureRecord,
} from '../../utils/fixtureAdmin';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const RemoveHeader = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #333;
  border-radius: 10px;
  background: #020617;
`;

type Step = 'select' | 'confirm' | 'success' | 'error';

const CACHE_TTL_MS = 5 * 60 * 1000;

const RemoveFixture: React.FC = () => {
  const [fixtures, setFixtures] = useState<FixtureRecord[]>([]);
  const [loadingFixtures, setLoadingFixtures] = useState(true);
  const [selectedFixtureId, setSelectedFixtureId] = useState('');
  const [step, setStep] = useState<Step>('select');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const session = getAdminSession();

  useEffect(() => {
    const loadFixtures = async () => {
      const activeSession = getAdminSession();
      if (!hasAdminCredentials(activeSession)) return;

      try {
        setLoadingFixtures(true);
        setErrorMessage(null);

        const items = await fetchItemsWithCache<FixtureRecord>(
          entriesUrl(FixturesTableName),
          CACHE_KEYS.fixtures,
          CACHE_TTL_MS
        );

        const sorted = [...(items ?? [])].sort((a, b) => {
          const dateCompare = a.date.localeCompare(b.date);
          if (dateCompare !== 0) return dateCompare;
          return a.time.localeCompare(b.time);
        });

        setFixtures(sorted);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load fixtures.';
        setErrorMessage(message);
      } finally {
        setLoadingFixtures(false);
      }
    };

    loadFixtures();
  }, [reloadKey]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const selectedFixture = fixtures.find((fixture) => getFixtureId(fixture) === selectedFixtureId);

  const handleRemoveClick = () => {
    setErrorMessage(null);

    if (!selectedFixtureId) {
      setErrorMessage('Select a fixture to remove.');
      return;
    }

    setStep('confirm');
  };

  const handleConfirmRemove = async () => {
    setErrorMessage(null);

    try {
      if (!hasAdminCredentials(session)) {
        throw new Error('Session expired. Please log in again.');
      }

      if (!selectedFixtureId) {
        throw new Error('No fixture selected.');
      }

      const response = await fetch(removeFixtureUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify({
          tableName: FixturesTableName,
          id: selectedFixtureId,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to remove fixture');
      }

      clearFixtureCaches();
      setStep('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while removing fixture';
      setErrorMessage(message);
      setStep('error');
    }
  };

  const resetToSelect = () => {
    setSelectedFixtureId('');
    setStep('select');
    setErrorMessage(null);
    setReloadKey((key) => key + 1);
  };

  if (step === 'confirm' && selectedFixture) {
    return (
      <PageContainer>
        <Title>Confirm Fixture Removal</Title>
        <RemoveHeader>
          <HelperText>This will permanently delete the selected fixture.</HelperText>
          <div style={{ marginTop: '0.75rem', color: 'var(--gray-50)' }}>
            {formatFixtureLabel(selectedFixture)}
          </div>
        </RemoveHeader>

        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

        <ActionsRow>
          <SecondaryButton type="button" onClick={() => setStep('select')}>
            Cancel
          </SecondaryButton>
          <SubmitButton type="button" onClick={handleConfirmRemove}>
            Remove fixture
          </SubmitButton>
        </ActionsRow>

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (step === 'success') {
    return (
      <PageContainer>
        <Title>Fixture Removed</Title>
        <Message>The fixture was removed successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetToSelect}>
            Remove another fixture
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (step === 'error') {
    return (
      <PageContainer>
        <Title>Fixture Not Removed</Title>
        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetToSelect}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Remove a Fixture</Title>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          handleRemoveClick();
        }}
      >
        <FieldGroup>
          <Label htmlFor="fixtureSelect">Fixture</Label>
          <Select
            id="fixtureSelect"
            value={selectedFixtureId}
            onChange={(e) => setSelectedFixtureId(e.target.value)}
            required
            disabled={loadingFixtures}
          >
            <option value="">Select fixture</option>
            {fixtures.map((fixture) => {
              const id = getFixtureId(fixture);
              return (
                <option key={id} value={id}>
                  {formatFixtureLabel(fixture)}
                </option>
              );
            })}
          </Select>
          {loadingFixtures ? <HelperText>Loading fixtures…</HelperText> : null}
        </FieldGroup>

        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

        <SubmitButton type="submit" disabled={loadingFixtures || !selectedFixtureId}>
          Remove fixture
        </SubmitButton>

        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default RemoveFixture;
